/**
 * All four customer-facing legal / policy pages.
 *
 * Routed by `App.js` on `/legal/:page` — one file rather than four so shared
 * layout / typography / TOC logic stays in one place. Each policy is a plain
 * function returning JSX (no state / no fetches) so pages render instantly.
 *
 * Content wording is fixed by the owner spec (18 Sept 2026 initial version).
 * Do NOT rewrite the legal wording without instruction.
 */
import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { POLICY_LAST_UPDATED_LABEL, SUPPORT_EMAIL } from "../lib/policies";
import { Mail, ArrowRight } from "lucide-react";

const supportMail = <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[var(--av-gold)] break-all">{SUPPORT_EMAIL}</a>;

// --------- shared layout ---------
function PolicyLayout({ id, title, sections }) {
  useEffect(() => {
    document.title = `${title} · Arthur's Vault`;
    window.scrollTo(0, 0);
  }, [title]);
  return (
    <div>
      <Header />
      <main className="pt-28 pb-16 px-4 sm:px-8 max-w-4xl mx-auto min-w-0" data-testid={`legal-page-${id}`}>
        <div className="mono-caption purple-text mb-2">Arthur's Vault · Legal</div>
        <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight mb-2">
          <span className="gold-text">{title}</span>
        </h1>
        <div className="text-xs text-[var(--av-text-dim)] mb-8">
          Last updated: <span data-testid="legal-last-updated">{POLICY_LAST_UPDATED_LABEL}</span>
        </div>

        {/* Table of contents */}
        {sections.length > 4 && (
          <nav aria-label="On this page" className="vault-card p-4 sm:p-5 mb-8 border-[var(--av-purple)]/40">
            <div className="mono-caption purple-text mb-3">On this page</div>
            <ol className="grid sm:grid-cols-2 gap-x-4 gap-y-1 text-sm list-decimal list-inside">
              {sections.map((s) => (
                <li key={s.id}>
                  <a href={`#${s.id}`} className="text-[var(--av-text-dim)] hover:text-[var(--av-gold)] break-words">
                    {s.title}
                  </a>
                </li>
              ))}
            </ol>
          </nav>
        )}

        <article className="legal-article space-y-8 text-[15px] leading-[1.75] text-[var(--av-text)]">
          {sections.map((s, i) => (
            <section key={s.id} id={s.id} className="scroll-mt-28">
              <h2 className="font-serif text-xl sm:text-2xl font-bold mb-3 text-[var(--av-gold)]">
                {i + 1}. {s.title}
              </h2>
              <div className="space-y-3 text-[var(--av-text-dim)]">{s.body}</div>
            </section>
          ))}
        </article>

        {/* Cross-links */}
        <div className="mt-14 pt-8 border-t border-[var(--av-border)]">
          <div className="mono-caption mb-3">Related Policies</div>
          <div className="grid sm:grid-cols-2 gap-3 text-sm">
            {[
              ["terms", "Terms & Conditions"],
              ["privacy", "Privacy Policy"],
              ["rewards", "Apex Rewards Terms"],
              ["refund", "Refund & Order Issues"],
            ].filter(([k]) => k !== id).map(([k, label]) => (
              <Link key={k} to={`/legal/${k}`} className="vault-card p-3 flex items-center justify-between hover:border-[var(--av-gold)]">
                <span>{label}</span>
                <ArrowRight className="w-4 h-4 text-[var(--av-gold)]"/>
              </Link>
            ))}
          </div>
          <div className="text-xs text-[var(--av-text-muted)] mt-6 flex items-center gap-2 flex-wrap">
            <Mail className="w-4 h-4 text-[var(--av-gold)]"/>
            Questions? Contact {supportMail} or open a support ticket from your Vault.
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

// --------- shared shortcuts ---------
const P = ({ children }) => <p>{children}</p>;
const UL = ({ items }) => (
  <ul className="list-disc list-outside pl-5 space-y-1 marker:text-[var(--av-purple)]">
    {items.map((it, i) => <li key={`${i}-${typeof it === "string" ? it.slice(0, 40) : i}`}>{it}</li>)}
  </ul>
);
const Quote = ({ children }) => (
  <blockquote className="border-l-2 border-[var(--av-purple)] pl-4 italic text-[var(--av-text)]">{children}</blockquote>
);

// ---------------------------------------------------------------------------
// TERMS & CONDITIONS
// ---------------------------------------------------------------------------
function Terms() {
  const sections = [
    { id: "about", title: "About Arthur's Vault", body: (<>
      <P>Arthur's Vault provides a service through which customers can purchase Apex Coins for use with supported game accounts.</P>
      <P>For questions, order issues or support, you can:</P>
      <UL items={[
        <>Create a support ticket through Arthur's Vault; or</>,
        <>Email {supportMail}</>,
      ]}/>
    </>)},
    { id: "eligibility", title: "Eligibility to Purchase", body: (<>
      <P>You must be at least 18 years old to make a purchase through Arthur's Vault, or have the permission of a parent or legal guardian.</P>
      <P>By placing an order, you confirm that you meet this requirement.</P>
      <P>You must create and sign into an Arthur's Vault account before making a purchase.</P>
      <P>You are responsible for maintaining the security of your account credentials and for activity carried out through your account.</P>
    </>)},
    { id: "account-info", title: "Account Information", body: (<>
      <P>You must provide accurate and up-to-date information when creating your account and placing orders. Depending on the service being used, this may include:</P>
      <UL items={["Your name", "Email address", "Governor UID", "Server", "In-game name"]}/>
      <P>You are responsible for checking your game account information carefully before placing an order. Incorrect information may prevent, delay or misdirect fulfilment.</P>
      <P>If you realise that you have provided incorrect information, contact Arthur's Vault support as soon as possible. We cannot guarantee that information can be changed once fulfilment has begun.</P>
    </>)},
    { id: "orders", title: "Orders", body: (<>
      <P>Before placing an order, you will be shown relevant information about the product, quantity and price. You are responsible for reviewing these details before confirming payment.</P>
      <P>Submitting payment does not entitle a customer to receive a product at an obviously incorrect price caused by a genuine technical, display or pricing error. If an order is affected by such an error, Arthur's Vault will contact the customer and take appropriate steps to resolve the matter.</P>
      <P>We may refuse or cancel an order where reasonably necessary, including where:</P>
      <UL items={[
        "Payment has not been successfully completed",
        "Required account information is incorrect or incomplete",
        "We reasonably suspect fraud, unauthorised payment or misuse",
        "An offer or purchase limit has been circumvented",
        "A technical or pricing error has occurred",
        "Fulfilment is unavailable",
        "Processing the order would breach applicable law",
      ]}/>
      <P>Where money has been taken for an order that cannot lawfully or properly be fulfilled, the matter will be handled in accordance with applicable consumer law and our <Link to="/legal/refund" className="text-[var(--av-gold)]">Refund & Order Issues Policy</Link>.</P>
    </>)},
    { id: "prices", title: "Prices and Payment", body: (<>
      <P>All prices displayed at checkout are shown in the currency indicated on the website.</P>
      <P>Payment must be made using one of the payment methods offered at checkout.</P>
      <P>Card payments are processed through Stripe. Arthur's Vault does not need to receive your full card details in order for Stripe to process your payment.</P>
      <P>The amount shown for your order at final checkout is the amount you agree to pay, subject to correction of obvious errors as described in these Terms.</P>
    </>)},
    { id: "fulfilment", title: "Fulfilment", body: (<>
      <P>Arthur's Vault uses an automated fulfilment process. Once an order has been successfully placed and payment confirmed, fulfilment may begin immediately.</P>
      <P>The Apex Coins will be delivered to the game account associated with the Governor UID and other game information supplied with the order.</P>
      <P>After fulfilment, you may be asked to confirm that you have received the Apex Coins by selecting "I have received the Apex" or a similar confirmation. You should only confirm receipt once you have checked your game account.</P>
      <P>Fulfilment times may vary because of technical issues, game systems, maintenance, third-party systems or other circumstances outside Arthur's Vault's reasonable control.</P>
      <P>If an order does not arrive as expected, do not repeatedly place replacement orders unless instructed to do so. Instead, create a support ticket or contact {supportMail}.</P>
    </>)},
    { id: "immediate", title: "Immediate Fulfilment and Cancellation", body: (<>
      <P>Arthur's Vault is designed to begin fulfilment as soon as possible after an order is successfully placed.</P>
      <P>Where required by applicable consumer law, you may be asked during checkout to expressly request immediate fulfilment and acknowledge the effect that beginning supply has on any applicable cancellation right.</P>
      <P>Once fulfilment has begun, an order generally cannot simply be cancelled or reversed because you have changed your mind. This does not affect any statutory rights you may have where, for example, digital content is faulty, not as described or otherwise does not comply with applicable consumer law.</P>
      <P>Please see our <Link to="/legal/refund" className="text-[var(--av-gold)]">Refund & Order Issues Policy</Link> for further information.</P>
    </>)},
    { id: "problems", title: "Delivery Problems and Incorrect Orders", body: (<>
      <P>If you believe:</P>
      <UL items={[
        "Your Apex Coins have not arrived",
        "The amount received is incorrect",
        "Your order was fulfilled incorrectly",
        "You were charged incorrectly",
        "A payment was duplicated",
        "Another problem has occurred with your order",
      ]}/>
      <P>please create a support ticket or contact {supportMail}. Include your order number and enough information for us to investigate the issue.</P>
      <P>Do not falsely confirm receipt if you have not received the order.</P>
    </>)},
    { id: "rewards", title: "Apex Rewards", body: (<>
      <P>Eligible purchases may earn Apex Rewards according to the programme rules in effect at the time of purchase. Use of Apex Rewards is governed by the separate <Link to="/legal/rewards" className="text-[var(--av-gold)]">Apex Rewards Terms</Link>.</P>
      <P>Apex Rewards are not cash and cannot be withdrawn or exchanged for cash. Certain products or special offers may earn Apex Rewards while not allowing existing Apex Rewards to be redeemed against that purchase.</P>
      <P>Promotional cashback or bonus Reward rates may apply for limited periods.</P>
    </>)},
    { id: "offers", title: "Offers and Purchase Limits", body: (<>
      <P>Arthur's Vault may provide special offers including Welcome, Daily, Weekly, Monthly, Vault, Streak, Challenge or other promotional offers.</P>
      <P>Offers may:</P>
      <UL items={[
        "Be limited to eligible accounts",
        "Have purchase limits",
        "Reset at specified times",
        "Be available for limited periods",
        "Have different Apex Rewards rules",
        "Be withdrawn or replaced",
      ]}/>
      <P>Arthur's Vault uses UTC (UTC+0) as the authoritative timezone for applicable time-based offer periods and resets.</P>
      <P>Customers must not attempt to bypass purchase limits or eligibility restrictions through multiple accounts, payment methods, browsers, devices or other means.</P>
    </>)},
    { id: "acceptable-use", title: "Acceptable Use", body: (<>
      <P>You must not:</P>
      <UL items={[
        "Use Arthur's Vault for fraudulent or unlawful purposes",
        "Attempt to gain unauthorised access to the website or another customer's account",
        "Interfere with the operation or security of the website",
        "Abuse promotions, Rewards or purchase limits",
        "Provide deliberately false information",
        "Use automated systems to exploit offers or the website",
        "Attempt to manipulate prices, orders or account balances",
        "Use multiple accounts primarily to circumvent restrictions",
      ]}/>
      <P>We may restrict or suspend access where reasonably necessary to investigate suspected fraud, abuse, security issues or serious breaches of these Terms.</P>
    </>)},
    { id: "closure", title: "Account Suspension or Closure", body: (<>
      <P>Arthur's Vault may suspend or restrict an account where reasonably necessary for security, fraud prevention, investigation of suspected abuse, legal compliance or serious breaches of these Terms.</P>
      <P>We will not remove legitimately earned Apex Rewards merely because an account has been inactive for a period of time.</P>
      <P>Where reasonably possible, account issues should be resolved through support.</P>
      <P>Nothing in these Terms prevents us from taking action required by law or necessary to protect Arthur's Vault, our customers or payment systems from fraud or abuse.</P>
    </>)},
    { id: "ip", title: "Intellectual Property and Third-Party Rights", body: (<>
      <P>Arthur's Vault's own website content, branding, design and original materials may not be copied, reproduced or commercially exploited without permission, except where permitted by law.</P>
      <P>References to games, game currencies, trademarks and other third-party intellectual property are used for identification and compatibility purposes.</P>
      <P>Unless expressly stated otherwise, third-party trademarks remain the property of their respective owners.</P>
    </>)},
    { id: "availability", title: "Website Availability", body: (<>
      <P>We aim to keep Arthur's Vault available and operating correctly, but uninterrupted availability cannot be guaranteed.</P>
      <P>We may temporarily restrict access for maintenance, security, updates, technical issues or circumstances outside our reasonable control.</P>
    </>)},
    { id: "liability", title: "Liability", body: (<>
      <P>Nothing in these Terms excludes or limits liability where doing so would be unlawful, including liability that cannot lawfully be excluded under applicable consumer protection law.</P>
      <P>Subject to applicable law, Arthur's Vault is not responsible for losses caused solely by circumstances outside our reasonable control or by inaccurate information supplied by the customer.</P>
      <P>Nothing in these Terms affects your statutory consumer rights.</P>
    </>)},
    { id: "changes", title: "Changes to These Terms", body: (<>
      <P>We may update these Terms when reasonably necessary, including to reflect changes to Arthur's Vault, applicable law, payment systems or our services.</P>
      <P>The latest version will be published on the website with its updated date. Where a change materially affects customers or an ongoing programme, we will take reasonable steps to provide appropriate notice.</P>
      <P>Changes will not retrospectively alter completed purchases unless required by law or agreed with the customer.</P>
    </>)},
    { id: "law", title: "Governing Law", body: (<>
      <P>These Terms are governed by the laws applicable in the United Kingdom, subject to any mandatory consumer protections that apply to you.</P>
      <P>Nothing in these Terms removes any right you may have to bring a claim in a court that has jurisdiction under applicable consumer law.</P>
    </>)},
    { id: "contact", title: "Contact", body: (<>
      <P>For questions about these Terms or Arthur's Vault:</P>
      <P>Arthur's Vault<br/>Email: {supportMail}</P>
      <P>You can also create a support ticket through your Arthur's Vault account.</P>
    </>)},
  ];
  return <PolicyLayout id="terms" title="Terms & Conditions" sections={sections} intro={<>
    <P>Welcome to Arthur's Vault.</P>
    <P>These Terms & Conditions ("Terms") govern your use of Arthur's Vault, including your account, purchases, orders, Apex Rewards and other services made available through the website.</P>
    <P>By creating an account, placing an order or otherwise using Arthur's Vault, you agree to these Terms. Please read them carefully before making a purchase.</P>
  </>}/>;
}

// ---------------------------------------------------------------------------
// PRIVACY POLICY
// ---------------------------------------------------------------------------
function Privacy() {
  const sections = [
    { id: "who", title: "Who We Are", body: (<>
      <P>Arthur's Vault operates the Arthur's Vault website and associated customer account and ordering services.</P>
      <P>For privacy questions or requests:</P>
      <P>Arthur's Vault<br/>{supportMail}</P>
    </>)},
    { id: "collect", title: "Information We Collect", body: (<>
      <P>Depending on how you use Arthur's Vault, we may collect and process:</P>
      <div><strong className="text-[var(--av-text)]">Account information</strong></div>
      <UL items={["Name", "Email address", "Account information and identifiers", "Account status", "Apex Rewards information"]}/>
      <div><strong className="text-[var(--av-text)]">Game and fulfilment information</strong></div>
      <UL items={["Governor UID", "Server", "In-game name", "Information necessary to fulfil and investigate orders"]}/>
      <div><strong className="text-[var(--av-text)]">Order information</strong></div>
      <UL items={["Products purchased", "Quantities", "Amounts paid", "Order dates/times", "Order status", "Fulfilment status", "Apex Rewards earned/redeemed", "Relevant order/support history"]}/>
      <div><strong className="text-[var(--av-text)]">Payment information</strong></div>
      <P>Payments are processed through Stripe. Arthur's Vault may receive payment-related information from Stripe such as payment status, transaction identifiers, payment method information and information necessary to manage or investigate transactions. Arthur's Vault does not need to store complete payment card details in order for Stripe to process card payments.</P>
      <div><strong className="text-[var(--av-text)]">Support information</strong></div>
      <P>If customers contact support, we may process:</P>
      <UL items={["Contact details", "Order number", "Messages", "Information supplied about the issue", "Relevant account/order/fulfilment information"]}/>
      <div><strong className="text-[var(--av-text)]">Technical/security information</strong></div>
      <P>Where necessary to operate and protect the website, limited technical information may be processed, including security logs, authentication information, IP addresses and device/browser information.</P>
      <P>Arthur's Vault does not currently use third-party behavioural advertising or analytics tools.</P>
    </>)},
    { id: "why", title: "Why We Use Information", body: (<>
      <P>Information may be used to:</P>
      <UL items={[
        "Create and maintain Arthur's Vault accounts",
        "Authenticate customers",
        "Process orders/payments",
        "Fulfil Apex orders",
        "Match orders with the correct game account",
        "Maintain order history",
        "Operate Apex Rewards",
        "Apply offer eligibility/purchase limits",
        "Provide support",
        "Investigate failed/incorrect orders",
        "Prevent fraud/abuse",
        "Protect website security",
        "Maintain transaction/business records",
        "Comply with legal obligations",
        "Send promotional communications where permitted",
      ]}/>
    </>)},
    { id: "bases", title: "Lawful Bases", body: (<>
      <P>Depending on the purpose, Arthur's Vault may rely on:</P>
      <div><strong className="text-[var(--av-text)]">Contract</strong></div>
      <P>Where processing is necessary to provide accounts, process/fulfil purchases, administer orders, operate purchase-related features and provide requested support.</P>
      <div><strong className="text-[var(--av-text)]">Legal obligation</strong></div>
      <P>Where information must be processed or retained to comply with applicable legal, accounting, tax or regulatory obligations.</P>
      <div><strong className="text-[var(--av-text)]">Legitimate interests</strong></div>
      <P>Where reasonably necessary for:</P>
      <UL items={[
        "Fraud prevention",
        "Security",
        "Protecting customers/Arthur's Vault",
        "Investigating payment/fulfilment problems",
        "Enforcing purchase limits",
        "Establishing/exercising/defending legal claims",
      ]}/>
      <div><strong className="text-[var(--av-text)]">Consent or other applicable marketing basis</strong></div>
      <P>Where consent is legally required, we request it appropriately. Customers can withdraw marketing consent or opt out of promotional communications at any time.</P>
    </>)},
    { id: "emails", title: "Promotional and Service Emails", body: (<>
      <P>We separate:</P>
      <div><strong className="text-[var(--av-text)]">Service communications</strong></div>
      <P>Important messages concerning:</P>
      <UL items={["Accounts", "Orders", "Payments", "Fulfilment", "Security", "Support", "Apex Rewards administration", "Important service changes"]}/>
      <div><strong className="text-[var(--av-text)]">Promotional communications</strong></div>
      <P>Promotions, events, offers and similar marketing.</P>
      <P>Customers can unsubscribe or opt out of promotional emails without preventing essential account/order/security/fulfilment communications. Customers are never unsubscribed from essential transactional communications merely because they opt out of marketing.</P>
    </>)},
    { id: "sharing", title: "Who Information May Be Shared With", body: (<>
      <P>Information is only shared where reasonably necessary. This may include:</P>
      <div><strong className="text-[var(--av-text)]">Stripe</strong> — for payment processing and payment-related functions.</div>
      <div><strong className="text-[var(--av-text)]">Fulfilment providers/systems</strong> — only information reasonably necessary for fulfilling Apex orders.</div>
      <div><strong className="text-[var(--av-text)]">Technology/service providers</strong> — hosting, infrastructure, communications, security or other providers reasonably necessary to operate Arthur's Vault.</div>
      <div><strong className="text-[var(--av-text)]">Legal/regulatory disclosures</strong> — where required by law or reasonably necessary for fraud investigation, legal rights or protection of Arthur's Vault/customers.</div>
      <P>Arthur's Vault does not sell personal information to advertisers.</P>
    </>)},
    { id: "international", title: "International Processing", body: (<>
      <P>Some providers may process information outside the United Kingdom. Where applicable law requires safeguards for international transfers, appropriate lawful safeguards are used.</P>
    </>)},
    { id: "retention", title: "Retention", body: (<>
      <P>Arthur's Vault accounts do not automatically expire simply because they have been inactive. This is important because accounts may contain:</P>
      <UL items={["Apex Rewards balances", "Purchase history", "Order records", "Information needed when customers return after a break"]}/>
      <P>We retain account information while the account remains open and for as long as reasonably necessary to operate the account and Apex Rewards programme.</P>
      <P>Other information may be retained where reasonably necessary for transactions, customer support, fraud prevention, security, disputes, legal claims and accounting/tax/legal obligations.</P>
      <P>We periodically review retained information and delete or anonymise information that is no longer reasonably required, subject to applicable legal requirements and the continued operation of the account/Rewards balance.</P>
      <P>Account inactivity alone does not cause Apex Rewards to expire.</P>
    </>)},
    { id: "rewards-data", title: "Apex Rewards Data", body: (<>
      <P>Apex Rewards information may include:</P>
      <UL items={["Rewards earned", "Rewards redeemed", "Rewards balance", "Rewards tier", "Qualifying spend", "Promotional Rewards activity"]}/>
      <P>We process this information as necessary to operate the programme.</P>
    </>)},
    { id: "rights", title: "Customer Rights", body: (<>
      <P>Depending on circumstances and applicable law, customers may have rights to:</P>
      <UL items={[
        "Access personal information",
        "Correct inaccurate information",
        "Request erasure in certain circumstances",
        "Request restriction in certain circumstances",
        "Object to certain processing",
        "Request portability where applicable",
        "Withdraw consent where applicable",
        "Object to direct marketing",
      ]}/>
      <P>Privacy requests should be sent to {supportMail}. Identity verification may be required where appropriate.</P>
    </>)},
    { id: "marketing", title: "Direct Marketing", body: (<>
      <P>Customers have the right to object to direct marketing. Promotional emails include a straightforward unsubscribe / opt-out mechanism.</P>
    </>)},
    { id: "deletion", title: "Account Deletion", body: (<>
      <P>Customers may contact Arthur's Vault to request account closure or deletion. Requests are handled according to applicable data protection law.</P>
      <P>Some information may need to be retained for legal obligations, transaction records, fraud prevention, disputes or legal claims.</P>
      <P>Because Apex Rewards are associated with the account, customers with an unused balance should contact support before requesting deletion.</P>
    </>)},
    { id: "security", title: "Security", body: (<>
      <P>We use reasonable technical and organisational measures to protect personal information against unauthorised access, loss, misuse, alteration or disclosure.</P>
    </>)},
    { id: "cookies", title: "Cookies", body: (<>
      <P>Arthur's Vault may use necessary cookies / similar technologies for:</P>
      <UL items={["Sign-in", "Security", "Sessions", "Checkout", "Necessary website state"]}/>
      <P>Arthur's Vault does not currently use third-party behavioural advertising or analytics tools. If non-essential analytics or advertising technologies are introduced later, we will update these disclosures and implement any legally required consent mechanism.</P>
    </>)},
    { id: "children", title: "Children and Purchases", body: (<>
      <P>Customers must be at least 18 years old to make a purchase, or have permission from a parent or legal guardian.</P>
    </>)},
    { id: "complaints", title: "Complaints", body: (<>
      <P>Customers with privacy concerns can contact Arthur's Vault first at {supportMail}.</P>
      <P>Customers may also complain to the UK's data protection regulator, the Information Commissioner's Office (ICO).</P>
    </>)},
    { id: "changes", title: "Changes", body: (<>
      <P>The latest Privacy Policy displays its updated date. Where a change materially affects use of personal information, we provide appropriate notice where required.</P>
    </>)},
    { id: "contact", title: "Contact", body: (<>
      <P>Arthur's Vault<br/>{supportMail}</P>
    </>)},
  ];
  return <PolicyLayout id="privacy" title="Privacy Policy" sections={sections}/>;
}

// ---------------------------------------------------------------------------
// APEX REWARDS TERMS
// ---------------------------------------------------------------------------
function Rewards() {
  const tiers = [
    ["Bronze", "£0+", "1%"],
    ["Silver", "£100+", "1.25%"],
    ["Gold", "£500+", "1.5%"],
    ["Platinum", "£1,000+", "2%"],
    ["Diamond", "£5,000+", "2.5%"],
    ["Apex Elite", "£10,000+", "3%"],
  ];
  const sections = [
    { id: "apex-rewards", title: "Apex Rewards", body: (<>
      <P>Eligible customers can earn Rewards from qualifying purchases and redeem available Rewards against eligible future purchases. Participation is linked to the Arthur's Vault account.</P>
    </>)},
    { id: "eligibility", title: "Eligibility", body: (<>
      <P>Customers require an Arthur's Vault account. Rewards are personal to the account. Rewards cannot normally be:</P>
      <UL items={["Sold", "Transferred", "Withdrawn as cash", "Exchanged for cash", "Used outside Arthur's Vault"]}/>
    </>)},
    { id: "tiers", title: "Current Rewards Tiers", body: (<>
      <div className="overflow-x-auto -mx-1">
        <table className="w-full text-sm min-w-[380px]">
          <thead>
            <tr className="text-left border-b border-[var(--av-border)]">
              <th className="py-2 pr-4 font-semibold text-[var(--av-text)]">Tier</th>
              <th className="py-2 pr-4 font-semibold text-[var(--av-text)]">Cumulative qualifying spend</th>
              <th className="py-2 font-semibold text-[var(--av-text)]">Rate</th>
            </tr>
          </thead>
          <tbody>
            {tiers.map(([name, spend, rate]) => (
              <tr key={name} className="border-b border-[var(--av-border)]/50">
                <td className="py-2 pr-4">{name}</td>
                <td className="py-2 pr-4 text-[var(--av-text-dim)]">{spend}</td>
                <td className="py-2 gold-text font-semibold">{rate}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <P>Tier is determined by qualifying cumulative spending recorded on the Arthur's Vault account.</P>
    </>)},
    { id: "earning", title: "Earning Rewards", body: (<>
      <P>Eligible purchases earn Apex Rewards according to the customer's applicable tier and any eligible promotion active at the time.</P>
      <P>Rewards may be withheld or adjusted where an order, payment or fulfilment issue is under investigation.</P>
    </>)},
    { id: "promo", title: "Event / Promotional Rewards", body: (<>
      <P>Arthur's Vault may run temporary additional Rewards promotions. Promotional Rewards may stack with standard tier Rewards where the promotion states that they do.</P>
      <P>Promotions may specify start/end times, eligible products, an additional rate, purchase limits and other conditions.</P>
      <P>Temporary promotional events do not permanently change the underlying Rewards tier.</P>
    </>)},
    { id: "redeeming", title: "Redeeming Rewards", body: (<>
      <P>Available Apex Rewards may be redeemed against eligible purchases. Unless otherwise specified, redemption on eligible normal purchases is limited to a maximum of <strong className="gold-text">5% of the eligible order value</strong>.</P>
      <P>Rewards cannot be redeemed for cash.</P>
    </>)},
    { id: "special", title: "Special Offers", body: (<>
      <P>Special products may earn Apex Rewards while preventing existing Rewards from being redeemed against the purchase. This may include:</P>
      <UL items={["Welcome Offer", "Daily Offer", "Weekly Offer", "Monthly Offer", "Vault Offers", "Streak Offers", "Challenge Offers", "Other special promotional products"]}/>
      <P>Where a special offer earns Rewards, normal tier Rewards and eligible event bonuses may apply according to the current programme rules.</P>
    </>)},
    { id: "availability", title: "Rewards Availability", body: (<>
      <P>Rewards are linked to valid qualifying purchases. Arthur's Vault may delay final crediting or use while confirming payment, fulfilment, order validity, whether the transaction has been reversed or refunded, and whether fraud or abuse is suspected.</P>
    </>)},
    { id: "refunds", title: "Refunds / Reversals", body: (<>
      <P>If an order is refunded, reversed, cancelled or otherwise unwound, Rewards earned from that order may be removed or adjusted. Rewards redeemed against an affected transaction may also be adjusted appropriately. See the <Link to="/legal/refund" className="text-[var(--av-gold)]">Refund & Order Issues Policy</Link>.</P>
    </>)},
    { id: "corrections", title: "Incorrect Rewards", body: (<>
      <P>Arthur's Vault may correct Rewards credited because of:</P>
      <UL items={["Technical error", "Duplicate credit", "Calculation error", "Reversed transaction", "Fraud", "Manipulation", "Programme abuse"]}/>
      <P>Legitimately earned Rewards will not be intentionally removed without valid reason.</P>
    </>)},
    { id: "expiry", title: "Expiry", body: (<>
      <P><strong className="text-[var(--av-text)]">Apex Rewards do not currently expire.</strong> Rewards do not disappear simply because a customer has been inactive. Arthur's Vault accounts do not automatically expire solely because of inactivity.</P>
      <P>If this policy changes in future, appropriate advance notice will be provided before applying an expiry policy to existing customers.</P>
    </>)},
    { id: "rates", title: "Changes to Rates / Tiers", body: (<>
      <P>Arthur's Vault may prospectively change:</P>
      <UL items={["Rewards percentages", "Tier thresholds", "Redemption limits", "Eligible products", "Promotional rules", "Programme features"]}/>
      <P>Material ongoing programme changes are communicated to affected customers within a reasonable period. Changes to earning rates apply prospectively and do not ordinarily recalculate legitimately earned Rewards from completed purchases. Temporary promotional events can begin and end according to their advertised period.</P>
    </>)},
    { id: "abuse", title: "Abuse", body: (<>
      <P>Customers must not manipulate Apex Rewards through:</P>
      <UL items={[
        "Multiple accounts used to circumvent restrictions",
        "Fraudulent transactions",
        "Artificial or reversed transactions designed to generate Rewards",
        "Technical exploits",
        "Duplicate Rewards",
        "Promotion manipulation",
        "Other deliberate abuse",
      ]}/>
      <P>Suspected abuse may justify temporary restriction while investigated. Improperly obtained Rewards may be removed.</P>
    </>)},
    { id: "no-cash", title: "No Cash Value", body: (<>
      <P>Apex Rewards are a loyalty benefit. They are not cash, a bank deposit, electronic money, an investment or a transferable monetary balance. They cannot be withdrawn to a bank, card or payment account.</P>
    </>)},
    { id: "closure", title: "Account Closure", body: (<>
      <P>Customers with unused Apex Rewards should contact support before requesting account closure. Rewards cannot be paid out as cash when an account closes.</P>
    </>)},
    { id: "end-programme", title: "Ending / Replacing Programme", body: (<>
      <P>Arthur's Vault may replace, suspend or discontinue Apex Rewards where reasonably necessary. If permanently discontinued, we aim to provide reasonable notice and information about using legitimately earned Rewards before closure, subject to applicable circumstances and law.</P>
    </>)},
    { id: "contact", title: "Contact", body: (<>
      <P>For Rewards questions: Arthur's Vault, {supportMail} or use the existing support ticket system.</P>
    </>)},
  ];
  return <PolicyLayout id="rewards" title="Apex Rewards Terms" sections={sections}/>;
}

// ---------------------------------------------------------------------------
// REFUND & ORDER ISSUES
// ---------------------------------------------------------------------------
function Refund() {
  const sections = [
    { id: "prompt-fulfilment", title: "Orders Begin Fulfilment Promptly", body: (<>
      <P>When a customer successfully places and pays for an order, Arthur's Vault may begin fulfilment immediately. Because fulfilment begins promptly, orders generally cannot be cancelled simply because the customer:</P>
      <UL items={[
        "Changed their mind",
        "Ordered more/less Apex than intended",
        "No longer wants the Apex",
        "Decided they prefer another product",
      ]}/>
      <P>Customers should check the product, quantity, Governor UID, server, in-game name and total price before paying.</P>
    </>)},
    { id: "immediate-supply", title: "Immediate Supply", body: (<>
      <P>Where required by applicable consumer law, customers are asked during checkout to expressly request immediate fulfilment and acknowledge the effect beginning supply has on any applicable cancellation right.</P>
      <P>This does not remove statutory rights where supplied content is faulty, not as described or otherwise fails applicable legal requirements.</P>
    </>)},
    { id: "problems", title: "Order Problems", body: (<>
      <P>Contact support if:</P>
      <UL items={[
        "Payment succeeded but Apex was not received",
        "Fulfilment appears to have failed",
        "An incorrect amount was received",
        "Your order may have been delivered incorrectly",
        "You were charged more than once",
        "Payment status is incorrect",
        "The order is stuck or delayed",
        "Another purchase issue occurs",
      ]}/>
      <P>Support options: the existing Arthur's Vault support ticket system, or {supportMail}. Please provide your order number and relevant details.</P>
    </>)},
    { id: "duplicates", title: "Duplicate Orders", body: (<>
      <P>Do not repeatedly repurchase an apparently delayed or failed order. Contact support first.</P>
    </>)},
    { id: "wrong-info", title: "Incorrect Game Information", body: (<>
      <P>Customers are responsible for checking Governor UID, server and in-game name. Because fulfilment may begin immediately, Arthur's Vault cannot guarantee that incorrect information can be changed after submission.</P>
      <P>Customers noticing an error should contact support immediately.</P>
    </>)},
    { id: "failed", title: "Failed / Incomplete Fulfilment", body: (<>
      <P>Where payment succeeds but fulfilment does not complete correctly, please contact support. Arthur's Vault will investigate the payment and fulfilment status and take appropriate steps to resolve the issue.</P>
      <P>Depending on circumstances and applicable rights, resolution may include correcting or completing fulfilment, correcting the order or account issue, or another appropriate remedy.</P>
    </>)},
    { id: "wrong-amount", title: "Incorrect Amount", body: (<>
      <P>Customers who believe they received an incorrect amount should not press "I have received the Apex" until they have checked delivery. Please create a support ticket.</P>
    </>)},
    { id: "duplicate-pay", title: "Duplicate Payments", body: (<>
      <P>Customers who believe they have been charged more than once should contact support. We will investigate the relevant orders and payments and take appropriate corrective action where an incorrect duplicate payment is confirmed.</P>
    </>)},
    { id: "completed", title: "Completed Orders", body: (<>
      <P>Where an order has been correctly fulfilled to the game account information supplied by the customer, it generally cannot be reversed merely because the customer later changes their mind. This does not affect applicable statutory rights.</P>
    </>)},
    { id: "rewards", title: "Rewards and Refunds", body: (<>
      <P>Where an order is refunded, reversed or unwound, Rewards earned may be removed or adjusted. Rewards redeemed against the affected transaction may also be adjusted appropriately. See the <Link to="/legal/rewards" className="text-[var(--av-gold)]">Apex Rewards Terms</Link>.</P>
    </>)},
    { id: "disputes", title: "Payment Disputes", body: (<>
      <P>Customers who do not recognise a payment or believe there is a transaction problem are encouraged to contact Arthur's Vault promptly. Contacting Arthur's Vault does not remove or restrict any rights available through payment providers or applicable law.</P>
    </>)},
    { id: "investigate", title: "Investigating Issues", body: (<>
      <P>Arthur's Vault may review payment status, order records, fulfilment records, Governor UID, server, in-game name, delivery status and relevant communications. Additional information may be requested where reasonably necessary.</P>
    </>)},
    { id: "statutory", title: "Statutory Rights", body: (<>
      <Quote>Nothing in this Refund & Order Issues Policy excludes, restricts or replaces rights or remedies that customers are entitled to under applicable consumer law.</Quote>
    </>)},
    { id: "contact", title: "Contact", body: (<>
      <P>Create a support ticket through Arthur's Vault, or email {supportMail}. Please include your order number wherever possible.</P>
    </>)},
  ];
  return <PolicyLayout id="refund" title="Refund & Order Issues Policy" sections={sections}/>;
}

// ---------------------------------------------------------------------------
// Router
// ---------------------------------------------------------------------------
export default function LegalRouter() {
  const { page } = useParams();
  switch ((page || "").toLowerCase()) {
    case "terms":
    case "terms-conditions":
      return <Terms/>;
    case "privacy":
    case "privacy-policy":
      return <Privacy/>;
    case "rewards":
    case "apex-rewards":
      return <Rewards/>;
    case "refund":
    case "refund-policy":
    case "refund-order-issues":
      return <Refund/>;
    default:
      return <Navigate to="/legal/terms" replace/>;
  }
}
